/**
 * InlineCriteriaSuggestions — "Suggested by AI from Thrive" (epic
 * `2026-10-30-ai-review-criteria-suggestions`, sub-task 05), behind
 * `?preview=suggest-inline`. The client chose this inline variant on
 * 2026-11-03; the button + dialog variant was removed.
 *
 * Under the criteria, in the violet `info` tokens with the sparkle the rest of
 * AI Review uses: selecting a list linked to a Thrive search asks
 * `POST /ai-reviews/criteria-suggestions` at once, with the offered groups
 * (the form's criteria are not sent). Once per selected list: editing the
 * criteria does not ask again (the API keeps each answer 24 h, so selecting
 * the list again later is cheap); selecting another list does.
 *
 * Nothing is written here: `onAdd` hands texts to the page, which merges them
 * with `mergeCarriedCriteria` and records where each criterion came from. A suggestion
 * whose text is in the form leaves the list and is only counted in one small
 * line, so adding one needs no state of its own. Copy and rules come from
 * `criteriaSuggestions.ts`.
 *
 * Compact by default (2026-11-02): the header collapses the whole section, so
 * every pending suggestion shows while it is open. An answer with nothing to
 * suggest (no decisions with text) starts collapsed, the header saying so; the
 * recruiter can still open it to read why.
 */
import type { CriteriaSignalGroup } from "@nu-assist/contracts";
import { Button, Spinner } from "@nu-assist/ui";
import { Check, ChevronDown, ChevronRight, Plus, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { AiReviewClient } from "../../../clients/aiReview.client";
import type { Criterion } from "./CriteriaPanel";
import { mergeCarriedCriteria } from "./carriedCriteria";
import {
  GROUP_LABEL,
  SIGNAL_GROUPS,
  addButtonCopy,
  asOfCopy,
  basedOnCopy,
  confidenceCopy,
  countsCopy,
  doesNotFitCopy,
  emptyCopy,
  evidenceCopy,
  isAlreadyInForm,
  noiseCopy,
  suggestErrorCopy,
} from "./criteriaSuggestions";
import { useSuggestCriteria } from "./useAiReviewRun";

export interface InlineCriteriaSuggestionsProps {
  client: AiReviewClient;
  /** The selected list, or `null` when none is. */
  importId: string | null;
  /** The list's Thrive search; `null` when the list is not linked to one. */
  searchIdThrive: string | null;
  existing: readonly Criterion[];
  max: number;
  onAdd: (texts: string[]) => void;
}

export function InlineCriteriaSuggestions({
  client,
  importId,
  searchIdThrive,
  existing,
  max,
  onAdd,
}: InlineCriteriaSuggestionsProps) {
  const suggest = useSuggestCriteria({ client });
  const [groups, setGroups] = useState<CriteriaSignalGroup[]>([...SIGNAL_GROUPS]);
  const [requestedGroups, setRequestedGroups] = useState<CriteriaSignalGroup[]>(groups);
  const [openRationale, setOpenRationale] = useState<ReadonlySet<number>>(new Set());
  // `null` = not chosen by the recruiter for this answer: open, unless the
  // answer has nothing to suggest.
  const [expandedChoice, setExpandedChoice] = useState<boolean | null>(null);

  const { mutate, reset } = suggest;
  function request(next: CriteriaSignalGroup[], refresh = false) {
    if (!importId) return;
    setRequestedGroups(next);
    setOpenRationale(new Set());
    setExpandedChoice(null);
    mutate({
      import_id: importId,
      groups: next,
      refresh,
    });
  }

  // The list this section last asked for: a re-render must not ask a second
  // time for the same list.
  const askedForRef = useRef<string | null>(null);
  // An unmount drops the mutation's observer, so a remount (StrictMode's in
  // dev) must ask again rather than wait on an answer it can no longer see.
  useEffect(
    () => () => {
      askedForRef.current = null;
    },
    []
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per selected linked list; Refresh re-requests.
  useEffect(() => {
    const list = importId && searchIdThrive ? `${importId}|${searchIdThrive}` : null;
    if (list === askedForRef.current) return;
    reset();
    if (!list) askedForRef.current = null;
    if (!list) return;
    askedForRef.current = list;
    const all = [...SIGNAL_GROUPS];
    setGroups(all);
    request(all);
  }, [importId, searchIdThrive]);

  if (!importId) return null;

  if (!searchIdThrive) {
    return (
      <p
        data-testid="inline-suggest-unlinked"
        className="mt-3 flex items-center gap-2 text-caption text-info"
      >
        <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
        Link this list to a Thrive search to get criteria suggested by AI.
      </p>
    );
  }

  const data = suggest.isPending ? undefined : suggest.data;
  const suggestions = data?.suggestions ?? [];
  // Keyed by the answer's own index, so test ids and "Why?" stay put as
  // suggestions are added and leave the list.
  const pending = suggestions
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => !isAlreadyInForm(s.text, existing));
  const addedCount = suggestions.length - pending.length;
  const nothingToSuggest = !!data && suggestions.length === 0;
  const expanded = expandedChoice ?? !nothingToSuggest;
  const toAdd = pending.map(({ s }) => s.text);
  // The page's own merge on a throwaway id factory: only `dropped` is read.
  const dropped = mergeCarriedCriteria(existing, toAdd, max, () => "").dropped;
  const fitting = toAdd.length - dropped;
  const groupsChanged =
    groups.length !== requestedGroups.length || groups.some((g) => !requestedGroups.includes(g));
  const noise = data ? noiseCopy(data.stats) : null;
  const doesNotFit = doesNotFitCopy(dropped, max);

  function toggleGroup(group: CriteriaSignalGroup) {
    setGroups((prev) =>
      prev.includes(group)
        ? prev.filter((g) => g !== group)
        : SIGNAL_GROUPS.filter((g) => g === group || prev.includes(g))
    );
  }

  return (
    <section
      aria-labelledby="inline-suggest-title"
      data-testid="inline-suggest"
      className="mt-4 rounded-md border border-info-strong bg-info-muted p-4"
    >
      <h3 id="inline-suggest-title" className="text-h3 text-info">
        <button
          type="button"
          data-testid="inline-suggest-toggle"
          aria-expanded={expanded}
          aria-controls="inline-suggest-body"
          className="flex w-full flex-wrap items-center gap-2 text-left"
          onClick={() => setExpandedChoice(!expanded)}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          Suggested by AI from Thrive
          <span className="rounded-pill border border-info-strong px-2 text-micro">AI</span>
          {!expanded && nothingToSuggest ? (
            <span
              data-testid="inline-suggest-collapsed-empty"
              className="text-caption text-text-secondary"
            >
              No decisions to learn from yet
            </span>
          ) : null}
          {!expanded && data && pending.length > 0 ? (
            <span
              data-testid="inline-suggest-collapsed-count"
              className="text-caption text-text-secondary"
            >
              {pending.length === 1 ? "1 suggestion" : `${pending.length} suggestions`}
            </span>
          ) : null}
        </button>
      </h3>
      <div id="inline-suggest-body" hidden={!expanded}>
        {/* Controls first (2026-11-03): Refresh and Add all sit under the title. */}
        <div className="mt-3 flex flex-wrap items-center gap-4 border-b border-info-strong pb-3">
          <fieldset className="flex flex-wrap items-center gap-4">
            <legend className="sr-only">Learn from rejections by</legend>
            <span className="text-caption text-text-secondary">Learn from rejections by</span>
            {SIGNAL_GROUPS.map((group) => (
              <label
                key={group}
                className="flex cursor-pointer items-center gap-2 text-body text-text"
              >
                <input
                  type="checkbox"
                  data-testid={`inline-suggest-group-${group}`}
                  className="size-4 accent-accent"
                  checked={groups.includes(group)}
                  disabled={suggest.isPending}
                  onChange={() => toggleGroup(group)}
                />
                {GROUP_LABEL[group]}
              </label>
            ))}
          </fieldset>
          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="secondary"
              data-testid="inline-suggest-refresh"
              title={groupsChanged ? undefined : "Read Thrive again and ask for new suggestions"}
              disabled={suggest.isPending || groups.length === 0 || (!groupsChanged && !data)}
              onClick={() => request(groups, !groupsChanged)}
            >
              Refresh
            </Button>
            <Button
              type="button"
              data-testid="inline-suggest-add-all"
              disabled={fitting === 0}
              onClick={() => onAdd(toAdd)}
            >
              {addButtonCopy(fitting)}
            </Button>
          </div>
        </div>
        {doesNotFit ? (
          <p data-testid="inline-suggest-does-not-fit" className="mt-2 text-body text-warning-ink">
            {doesNotFit}
          </p>
        ) : null}
        <p data-testid="inline-suggest-as-of" className="mt-1 text-caption text-text-secondary">
          {asOfCopy(data?.generated_at)}
        </p>

        {suggest.isPending ? (
          // biome-ignore lint/a11y/useSemanticElements: <output> is not a status line.
          <div
            role="status"
            data-testid="inline-suggest-loading"
            className="mt-3 flex items-center gap-3 text-body text-text-secondary"
          >
            <Spinner size="md" label="Reading decisions" />
            Reading the search's decisions in Thrive…
          </div>
        ) : null}

        {suggest.isError ? (
          <div
            role="alert"
            data-testid="inline-suggest-error"
            className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
          >
            {suggestErrorCopy(suggest.error)}
            <Button
              type="button"
              variant="secondary"
              data-testid="inline-suggest-retry"
              onClick={() => request(requestedGroups)}
            >
              Try again
            </Button>
          </div>
        ) : null}

        {data ? (
          <div className="mt-3 flex flex-col gap-3">
            <p data-testid="inline-suggest-counts" className="text-caption text-text-secondary">
              {countsCopy(data.stats)}
            </p>
            {noise ? <p className="text-caption text-text-secondary">{noise}</p> : null}

            {suggestions.length === 0 ? (
              <p data-testid="inline-suggest-empty" className="text-body text-text">
                {emptyCopy(data.stats)}
              </p>
            ) : (
              <ul className="flex flex-col gap-2" aria-label="Criteria suggested by AI">
                {pending.map(({ s, i }) => {
                  const showWhy = openRationale.has(i);
                  return (
                    <li
                      key={s.text}
                      data-testid={`inline-suggestion-${i}`}
                      className="rounded-md border border-info-strong bg-surface p-3"
                    >
                      <div className="flex items-start gap-3">
                        <Sparkles
                          className="mt-0.5 h-4 w-4 shrink-0 text-info"
                          aria-hidden="true"
                        />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-body text-text">{s.text}</span>
                          <span className="text-caption text-text-secondary">
                            {[
                              confidenceCopy(s.confidence),
                              basedOnCopy(s.based_on),
                              evidenceCopy(s.evidence_count),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                          {s.rationale || s.quotes?.length ? (
                            <div>
                              <button
                                type="button"
                                data-testid={`inline-suggestion-why-${i}`}
                                aria-expanded={showWhy}
                                className="text-caption text-info hover:underline"
                                onClick={() =>
                                  setOpenRationale((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(i)) next.delete(i);
                                    else next.add(i);
                                    return next;
                                  })
                                }
                              >
                                {showWhy ? "Hide why" : "Why?"}
                              </button>
                              {showWhy ? (
                                <div className="mt-1 flex flex-col gap-1 text-caption text-text-secondary">
                                  {s.rationale ? <p>{s.rationale}</p> : null}
                                  {(s.quotes ?? []).map((q) => (
                                    <blockquote
                                      key={q}
                                      className="border-l-2 border-info-strong pl-2 italic"
                                    >
                                      “{q}”
                                    </blockquote>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          data-testid={`inline-suggestion-add-${i}`}
                          aria-label={`Add criterion: ${s.text}`}
                          disabled={
                            mergeCarriedCriteria(existing, [s.text], max, () => "").dropped > 0
                          }
                          onClick={() => onAdd([s.text])}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          Add
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {addedCount > 0 ? (
              <p
                data-testid="inline-suggest-added"
                className="flex items-center gap-1 text-micro text-text-secondary"
              >
                <Check className="h-3 w-3 text-success-ink" aria-hidden="true" />
                {pending.length === 0
                  ? "All suggestions added to the criteria"
                  : `${addedCount} added to the criteria`}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
